import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Save, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { COMPETITIONS, WORKSHOPS, API_BASE } from "@/lib/festival";

// Reverse map Arabic label -> id for chips
const compByLabel = Object.fromEntries(COMPETITIONS.map((c) => [c.label, c.id]));
const wsByLabel = Object.fromEntries(WORKSHOPS.map((w) => [w.label, w.id]));

export default function EditRegistrationModal({ open, row, token, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setForm({
      full_name: row.full_name || "",
      age: row.age || "",
      school_name: row.school_name || "",
      area: row.area || "",
      companion_name: row.companion_name || "",
      companion_phone: row.companion_phone || "",
      health_issue: !!row.health_issue,
      health_issue_details: row.health_issue_details || "",
      psych_issue: !!row.psych_issue,
      psych_issue_details: row.psych_issue_details || "",
      competitions: (row.competitions || []).map((l) => compByLabel[l] || l),
      workshops: (row.workshops || []).map((l) => wsByLabel[l] || l),
    });
  }, [row]);

  if (!open || !form) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, id) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        competitions: form.competitions.map((id) => COMPETITIONS.find((c) => c.id === id)?.label || id),
        workshops: form.workshops.map((id) => WORKSHOPS.find((w) => w.id === id)?.label || id),
      };
      const { data } = await axios.patch(`${API_BASE}/registrations/${row.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم تحديث بيانات الطفل");
      onSaved(data);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="absolute inset-0 bg-[#1E293B]/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form
        onSubmit={save}
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 border-2 border-[#FFD600]"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-black text-[#1E293B]">تعديل بيانات الطفل</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-[#FFFDF0] hover:bg-[#FFD600] flex items-center justify-center" data-testid="edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="الاسم الثلاثي" value={form.full_name} onChange={(v) => update("full_name", v)} testid="edit-full-name" />
          <Input label="العمر" type="number" value={form.age} onChange={(v) => update("age", v)} testid="edit-age" />
          <Input label="المدرسة" value={form.school_name} onChange={(v) => update("school_name", v)} testid="edit-school" />
          <Input label="المنطقة" value={form.area} onChange={(v) => update("area", v)} testid="edit-area" />
          <Input label="اسم المرافق" value={form.companion_name} onChange={(v) => update("companion_name", v)} testid="edit-companion-name" />
          <Input label="هاتف المرافق" value={form.companion_phone} onChange={(v) => update("companion_phone", v)} dir="ltr" testid="edit-companion-phone" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <YesNo label="مرض صحي؟" value={form.health_issue} onChange={(v) => update("health_issue", v)} testid="edit-health" />
          <YesNo label="حالة نفسية؟" value={form.psych_issue} onChange={(v) => update("psych_issue", v)} testid="edit-psych" />
        </div>

        {form.health_issue && (
          <textarea className="mt-3 w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-[#FFFDF0] font-semibold" rows={2}
            placeholder="تفاصيل صحية" value={form.health_issue_details} onChange={(e) => update("health_issue_details", e.target.value)} />
        )}
        {form.psych_issue && (
          <textarea className="mt-3 w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-[#FFFDF0] font-semibold" rows={2}
            placeholder="تفاصيل نفسية" value={form.psych_issue_details} onChange={(e) => update("psych_issue_details", e.target.value)} />
        )}

        <Chips title="المسابقات" items={COMPETITIONS} selected={form.competitions} onToggle={(id) => toggleArr("competitions", id)} activeClass="active-blue" />
        <Chips title="الورش" items={WORKSHOPS} selected={form.workshops} onToggle={(id) => toggleArr("workshops", id)} activeClass="active-red" />

        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-outline !py-2 !px-5">إلغاء</button>
          <button type="submit" disabled={saving} className="btn-3d !py-2.5 !px-6" data-testid="edit-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> حفظ</>}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", dir, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-black text-[#1E293B] mb-1 block">{label}</span>
      <input
        type={type}
        dir={dir}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] bg-[#FFFDF0] font-semibold focus:border-[#0066CC] focus:outline-none ${dir === "ltr" ? "text-left" : ""}`}
        data-testid={testid}
      />
    </label>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-[#FFFDF0] p-3" data-testid={testid}>
      <div className="text-xs font-black text-[#1E293B] mb-2">{label}</div>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange(true)}
          className={`flex-1 py-2 rounded-lg font-black text-sm ${value ? "bg-[#E63946] text-white" : "bg-white border border-[#E2E8F0]"}`}>نعم</button>
        <button type="button" onClick={() => onChange(false)}
          className={`flex-1 py-2 rounded-lg font-black text-sm ${!value ? "bg-[#10B981] text-white" : "bg-white border border-[#E2E8F0]"}`}>لا</button>
      </div>
    </div>
  );
}

function Chips({ title, items, selected, onToggle, activeClass }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-black text-[#1E293B] mb-2">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => {
          const active = selected.includes(it.id);
          return (
            <button key={it.id} type="button" onClick={() => onToggle(it.id)} className={`chip !py-1.5 !px-3 text-sm ${active ? activeClass : ""}`}>
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
